import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { enquiryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/Toast';
import { LABELS, SKILL_LIST } from '../../constants/enquiryFormOptions';
import { fieldStyle, StatusBadge } from '../../utils/enquiryAdminUi';

function DetailSection({ title, children }) {
  return (
    <div
      className="card"
      style={{ marginBottom: '16px' }}
    >
      <h3
        style={{
          color: '#667eea',
          fontSize: '16px',
          marginBottom: '16px',
          marginTop: 0,
          paddingBottom: '8px',
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 200px) 1fr',
        gap: '8px 16px',
        alignItems: 'start',
      }}
    >
      <span style={{ fontSize: '13px', fontWeight: '600', color: '#718096' }}>{label}</span>
      <span style={{ fontSize: '14px', color: '#2d3748', wordBreak: 'break-word' }}>
        {String(value)}
      </span>
    </div>
  );
}

export default function EnquiryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [enquiry, setEnquiry] = useState(null);
  const [logs, setLogs] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [counseling, setCounseling] = useState({});
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    enquiryAPI
      .get(id)
      .then((res) => {
        setEnquiry(res.data.enquiry);
        setLogs(res.data.followUpLogs || []);
        setCounseling(res.data.enquiry.counseling || {});
        setStatus(res.data.enquiry.status);
      })
      .catch(() => showToast('Failed to load enquiry', 'error'));
  };

  useEffect(() => {
    load();
    if (user?.role === 'admin') {
      enquiryAPI.getAssignableStaff().then((res) => setCounselors(res.data.staff));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.role]);

  const saveCounseling = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await enquiryAPI.updateCounseling(id, {
        ...counseling,
        feeDiscussed: counseling.feeDiscussed
          ? Number(counseling.feeDiscussed)
          : undefined,
        discountOffered: counseling.discountOffered
          ? Number(counseling.discountOffered)
          : undefined,
        converted: counseling.converted === true || counseling.converted === 'true',
        logNote: counseling.logNote,
        logAction: 'counseling_update',
      });
      showToast('Counseling notes saved', 'success');
      load();
    } catch {
      showToast('Failed to save counseling notes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async () => {
    try {
      await enquiryAPI.updateStatus(id, { status });
      showToast('Status updated', 'success');
      load();
    } catch {
      showToast('Failed to update status', 'error');
    }
  };

  const handleAssign = async (counselorId) => {
    try {
      await enquiryAPI.assign(id, counselorId);
      showToast('Assignment updated', 'success');
      load();
    } catch {
      showToast('Failed to assign staff', 'error');
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Delete enquiry ${enquiry.enquiryNumber} for ${enquiry.personal?.fullName}? This action cannot be undone.`
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await enquiryAPI.remove(id);
      showToast('Enquiry deleted', 'success');
      navigate('/admin/enquiries');
    } catch {
      showToast('Failed to delete enquiry', 'error');
      setDeleting(false);
    }
  };

  if (!enquiry) {
    return <div className="loading">Loading enquiry…</div>;
  }

  const e = enquiry;

  return (
    <div className="container">
      <div style={{ marginBottom: '16px' }}>
        <Link
          to="/admin/enquiries"
          style={{
            color: '#667eea',
            fontSize: '14px',
            fontWeight: '600',
            textDecoration: 'none',
          }}
        >
          ← Back to enquiries
        </Link>
      </div>

      <div className="card" style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '8px' }}>
              {e.personal?.fullName}
            </h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: '0 0 8px' }}>
              <strong style={{ color: '#667eea' }}>{e.enquiryNumber}</strong>
              {' · '}
              Submitted {new Date(e.submittedAt).toLocaleString()}
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <StatusBadge
                variant="status"
                value={e.status}
                label={LABELS.status[e.status]}
              />
              {e.counseling?.interestLevel && (
                <StatusBadge
                  variant="interest"
                  value={e.counseling.interestLevel}
                  label={LABELS.interestLevel[e.counseling.interestLevel]}
                />
              )}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <select
              value={status}
              onChange={(ev) => setStatus(ev.target.value)}
              style={{ ...fieldStyle, minWidth: '140px' }}
            >
              {Object.entries(LABELS.status).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={saveStatus}
              style={{ textTransform: 'none' }}
            >
              Update status
            </button>
            {user?.role === 'admin' && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
                style={{ textTransform: 'none' }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      </div>

      {user?.role === 'admin' && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Assigned counselor</label>
            <select
              value={e.assignedCounselor?._id || ''}
              onChange={(ev) => handleAssign(ev.target.value || null)}
              style={fieldStyle}
            >
              <option value="">Unassigned</option>
              {counselors.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '0',
          marginBottom: '16px',
        }}
      >
        <DetailSection title="Personal">
          <DetailRow label="Full name" value={e.personal?.fullName} />
          <DetailRow label="DOB" value={e.personal?.dateOfBirth?.slice?.(0, 10)} />
          <DetailRow label="Gender" value={LABELS.gender[e.personal?.gender]} />
          <DetailRow label="Mobile" value={e.personal?.mobile} />
          <DetailRow label="Email" value={e.personal?.email} />
          <DetailRow
            label="Address"
            value={`${e.personal?.address?.city}, ${e.personal?.address?.state}`}
          />
        </DetailSection>

        <DetailSection title="Education & Work">
          <DetailRow
            label="Qualification"
            value={
              e.education?.highestQualification === 'other'
                ? e.education?.highestQualificationOther || 'Other'
                : LABELS.qualification[e.education?.highestQualification]
            }
          />
          <DetailRow label="College" value={e.education?.collegeName} />
          <DetailRow label="Branch" value={e.education?.branch} />
          <DetailRow
            label="Experience"
            value={LABELS.experience[e.work?.totalExperience]}
          />
          <DetailRow label="Company" value={e.work?.lastCompany} />
        </DetailSection>

        <DetailSection title="Course preference">
          <DetailRow
            label="Course"
            value={LABELS.course[e.coursePreference?.courseInterested]}
          />
          <DetailRow
            label="Batch"
            value={LABELS.batchMode[e.coursePreference?.batchMode]}
          />
          <DetailRow label="Start month" value={e.coursePreference?.preferredStartMonth} />
        </DetailSection>

        <DetailSection title="Career goals">
          <DetailRow label="Why join" value={e.careerGoals?.whyJoin} />
          <DetailRow label="2–3 year goal" value={e.careerGoals?.careerGoal2To3Years} />
          <DetailRow
            label="Looking for"
            value={e.careerGoals?.lookingFor
              ?.map((k) => LABELS.lookingFor[k])
              .join(', ')}
          />
        </DetailSection>
      </div>

      <div className="card" style={{ marginBottom: '16px' }}>
        <h3
          style={{
            color: '#667eea',
            fontSize: '16px',
            marginBottom: '16px',
            marginTop: 0,
            paddingBottom: '8px',
            borderBottom: '2px solid #e2e8f0',
          }}
        >
          Skill ratings
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#2d3748',
                  }}
                >
                  Skill
                </th>
                <th
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#2d3748',
                  }}
                >
                  Level
                </th>
              </tr>
            </thead>
            <tbody>
              {SKILL_LIST.map((skill) => {
                const rating = e.skillRatings?.find((r) => r.skill === skill);
                return (
                  <tr key={skill} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: '#2d3748' }}>
                      {LABELS.skill[skill]}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: '#4a5568' }}>
                      {rating?.level ? LABELS.skillLevel[rating.level] : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <form className="card" onSubmit={saveCounseling} style={{ marginBottom: '16px' }}>
        <h2
          style={{
            color: '#667eea',
            fontSize: '18px',
            marginBottom: '20px',
            marginTop: 0,
          }}
        >
          Counseling notes (internal)
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0 16px',
          }}
        >
          <div className="form-group">
            <label>Date of inquiry</label>
            <input
              type="date"
              value={counseling.dateOfInquiry?.slice?.(0, 10) || ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, dateOfInquiry: ev.target.value }))
              }
              style={fieldStyle}
            />
          </div>
          <div className="form-group">
            <label>Interest level</label>
            <select
              value={counseling.interestLevel || ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, interestLevel: ev.target.value }))
              }
              style={fieldStyle}
            >
              <option value="">Select…</option>
              {Object.entries(LABELS.interestLevel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Suggested course</label>
            <input
              value={counseling.suggestedCourse || ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, suggestedCourse: ev.target.value }))
              }
              style={fieldStyle}
            />
          </div>
          <div className="form-group">
            <label>Suggested batch</label>
            <input
              value={counseling.suggestedBatch || ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, suggestedBatch: ev.target.value }))
              }
              style={fieldStyle}
            />
          </div>
          <div className="form-group">
            <label>Follow-up date</label>
            <input
              type="date"
              value={counseling.followUpDate?.slice?.(0, 10) || ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, followUpDate: ev.target.value }))
              }
              style={fieldStyle}
            />
          </div>
          <div className="form-group">
            <label>Fee discussed (₹)</label>
            <input
              type="number"
              value={counseling.feeDiscussed ?? ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, feeDiscussed: ev.target.value }))
              }
              style={fieldStyle}
            />
          </div>
          <div className="form-group">
            <label>Discount offered (₹)</label>
            <input
              type="number"
              value={counseling.discountOffered ?? ''}
              onChange={(ev) =>
                setCounseling((c) => ({ ...c, discountOffered: ev.target.value }))
              }
              style={fieldStyle}
            />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '10px',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={!!counseling.converted}
                onChange={(ev) =>
                  setCounseling((c) => ({ ...c, converted: ev.target.checked }))
                }
              />
              <span>Converted</span>
            </label>
          </div>
        </div>
        <div className="form-group">
          <label>Remarks</label>
          <textarea
            rows={4}
            value={counseling.remarks || ''}
            onChange={(ev) => setCounseling((c) => ({ ...c, remarks: ev.target.value }))}
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        </div>
        <div className="form-group">
          <label>Activity log note (optional)</label>
          <input
            value={counseling.logNote || ''}
            onChange={(ev) => setCounseling((c) => ({ ...c, logNote: ev.target.value }))}
            style={fieldStyle}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
          style={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save counseling notes'}
        </button>
      </form>

      {logs.length > 0 && (
        <div className="card">
          <h3
            style={{
              color: '#667eea',
              fontSize: '16px',
              marginBottom: '16px',
              marginTop: 0,
            }}
          >
            Activity log
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {logs.map((log) => (
              <li
                key={log._id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: '13px',
                  color: '#4a5568',
                }}
              >
                <strong style={{ color: '#2d3748' }}>{log.action}</strong>
                {' — '}
                {log.counselorId?.name}
                {' — '}
                {new Date(log.createdAt).toLocaleString()}
                {log.note && (
                  <p style={{ margin: '6px 0 0', color: '#718096' }}>{log.note}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
